import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn, Search, ShieldCheck, RefreshCw, History, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate, fmtINR, errMsg } from "../../lib/api";
import { useAuth, DASHBOARD_HOME } from "../../context/AuthContext";
import { PageHeader, StatusBadge, TableShell, TR, TD, LoadingRows } from "../../components/Shared";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const TABS = [
  { key: "SUPER_ADMIN", label: "Super Admins" },
  { key: "PARTNER", label: "Partners" },
  { key: "BUSINESS_OWNER", label: "Business Owners" },
  { key: "FREELANCER", label: "Freelancers" },
  { key: "CREATOR", label: "Creators" },
];
const FILTERS = [
  { key: "", label: "All" }, { key: "active", label: "Active" }, { key: "expiring", label: "Expiring Soon" },
  { key: "expired", label: "Expired" }, { key: "suspended", label: "Suspended" }, { key: "renewal_pending", label: "Renewal Pending" },
];
const ROLE_BADGE = { SUPER_ADMIN: "Super Admin", PARTNER: "Partner", BUSINESS_OWNER: "Store Admin", CUSTOMER: "Freelancer" };
const RENEW_DAYS = { SUPER_ADMIN: 30, PARTNER: 30, BUSINESS_OWNER: 365, CUSTOMER: 30 };

const DayChip = ({ days }) => {
  if (days == null) return <span className="text-xs text-muted-foreground">—</span>;
  const cls = days <= 0 ? "bg-rose-500/15 text-rose-500" : days <= 2 ? "bg-rose-500/15 text-rose-500" : days <= 5 ? "bg-amber-500/15 text-amber-600" : "bg-emerald-500/15 text-emerald-600";
  return <span className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold ${cls}`} data-testid="days-left-chip">
    {days <= 0 ? "Expired" : `${days} Day${days === 1 ? "" : "s"} Left`}
  </span>;
};

export default function RootAccess() {
  const [tab, setTab] = useState("SUPER_ADMIN");
  const [state, setState] = useState("");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [accounts, setAccounts] = useState(null);
  const [busy, setBusy] = useState(null);
  const [renewTarget, setRenewTarget] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const { impersonate } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { const t = setTimeout(() => setQ(qInput), 300); return () => clearTimeout(t); }, [qInput]);

  const load = useCallback(() => {
    api.get("/root/accounts", { params: { role: tab, q, state } })
      .then((r) => setAccounts(r.data.accounts))
      .catch((e) => toast.error(errMsg(e)));
  }, [tab, q, state]);
  useEffect(() => { setAccounts(null); load(); }, [load]);

  const access = async (acc) => {
    setBusy(acc.id);
    try {
      const r = await api.post("/root/impersonate", { user_id: acc.id });
      impersonate(r.data);
      toast.success(`Now viewing as ${r.data.user.name}`);
      navigate(DASHBOARD_HOME[r.data.user.role] || "/");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };

  const renew = async () => {
    setBusy(renewTarget.id);
    try {
      await api.post(`/root/accounts/${renewTarget.id}/renew`);
      toast.success(`${renewTarget.name} renewed for ${RENEW_DAYS[renewTarget.role]} days — access restored`);
      setRenewTarget(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="root-access">
      <PageHeader title="Account Access & Renewals" subtitle="Every account — active, expiring, expired or suspended — stays visible here forever. Expiry never deletes data.">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="account-search" placeholder="Search name, email or phone…" value={qInput}
            onChange={(e) => setQInput(e.target.value)} className="pl-9 w-64 rounded-full" />
        </div>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-3" data-testid="access-tabs">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`tab-${t.key.toLowerCase()}`} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${tab === t.key ? "gradient-bg text-white glow-sm" : "border border-border text-muted-foreground hover:text-foreground"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="access-filters">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`filter-${f.key || "all"}`} onClick={() => setState(f.key)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-extrabold transition-colors ${state === f.key ? "bg-foreground text-background" : "border border-border text-muted-foreground hover:text-foreground"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {!accounts ? <LoadingRows rows={5} /> : tab === "FREELANCER" ? (
        <TableShell testid="accounts-table" head={["Freelancer", "Base Role", "Profile & Stats", "Subscription", "Listings", "Status", "Actions"]}>
          {accounts.length === 0 && <TR testid="accounts-empty"><TD><p className="text-sm text-muted-foreground py-4">No freelancers found for this filter.</p></TD><TD /><TD /><TD /><TD /><TD /><TD /></TR>}
          {accounts.map((a) => {
            const f = a.freelancer_info || {};
            return (
              <TR key={a.id} testid={`account-row-${a.id}`}>
                <TD><div><p className="font-bold text-sm">{f.display_name || a.name}</p><p className="text-xs text-muted-foreground">{a.email}{a.phone ? ` · ${a.phone}` : ""}</p></div></TD>
                <TD><span className="text-xs font-bold px-2.5 py-1 rounded-full border border-border">Freelancer{a.role === "PARTNER" ? " · Partner" : ""}</span></TD>
                <TD className="text-xs font-semibold text-muted-foreground">
                  <p className="text-foreground">{f.title || "—"}</p>
                  <p>@{f.slug} · ★ {f.rating} ({f.reviews}) · {f.completed_orders} orders{f.location ? ` · ${f.location}` : ""}</p>
                </TD>
                <TD>
                  {f.subscription_until
                    ? <span className={`px-2.5 py-1 rounded-full text-[11px] font-extrabold ${new Date(f.subscription_until) > new Date() ? "bg-emerald-500/15 text-emerald-600" : "bg-rose-500/15 text-rose-500"}`} data-testid={`fl-sub-chip-${a.id}`}>
                        {new Date(f.subscription_until) > new Date() ? `Active till ${fmtDate(f.subscription_until)}` : "Expired"}
                      </span>
                    : <span className="text-xs text-muted-foreground">—</span>}
                </TD>
                <TD className="text-xs font-semibold">{f.listings ?? 0} listing{(f.listings ?? 0) === 1 ? "" : "s"}</TD>
                <TD><StatusBadge status={a.account_state} /></TD>
                <TD>
                  <button data-testid={`access-account-${a.id}`} disabled={busy === a.id} onClick={() => access(a)} title="Access Account"
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-extrabold gradient-bg text-white hover:opacity-85 transition-opacity disabled:opacity-50 glow-sm">
                    <LogIn className="h-3.5 w-3.5" /> {busy === a.id ? "Opening…" : "Access"}
                  </button>
                </TD>
              </TR>
            );
          })}
        </TableShell>
      ) : tab === "CREATOR" ? (
        <TableShell testid="accounts-table" head={["Creator", "Videos", "Views", "Affiliate Clicks", "Conversions", "Earnings", "Status", "Actions"]}>
          {accounts.length === 0 && <TR testid="accounts-empty"><TD><p className="text-sm text-muted-foreground py-4">No creators found for this filter.</p></TD><TD /><TD /><TD /><TD /><TD /><TD /><TD /></TR>}
          {accounts.map((a) => {
            const c = a.creator_info || {};
            return (
              <TR key={a.id} testid={`account-row-${a.id}`}>
                <TD><div><p className="font-bold text-sm">{a.name}</p><p className="text-xs text-muted-foreground">{a.email}{a.phone ? ` · ${a.phone}` : ""}{c.slug ? ` · @${c.slug}` : ""}</p></div></TD>
                <TD className="text-xs font-semibold" data-testid={`creator-videos-${a.id}`}>{c.videos ?? 0} video{(c.videos ?? 0) === 1 ? "" : "s"}</TD>
                <TD className="text-xs font-semibold" data-testid={`creator-views-${a.id}`}>{c.views ?? 0}</TD>
                <TD className="text-xs font-semibold" data-testid={`creator-clicks-${a.id}`}>{c.clicks ?? 0}</TD>
                <TD className="text-xs font-semibold" data-testid={`creator-convs-${a.id}`}>{c.conversions ?? 0}</TD>
                <TD className="text-xs font-semibold" data-testid={`creator-earnings-${a.id}`}>
                  <p className="font-extrabold text-emerald-600">{fmtINR(c.earnings_total ?? 0)}</p>
                  <p className="text-[10px] text-muted-foreground">P: {fmtINR(c.earnings_pending ?? 0)} · A: {fmtINR(c.earnings_approved ?? 0)} · Paid: {fmtINR(c.earnings_paid ?? 0)}</p>
                </TD>
                <TD><StatusBadge status={a.account_state} /></TD>
                <TD>
                  <button data-testid={`access-account-${a.id}`} disabled={busy === a.id} onClick={() => access(a)} title="Access Account"
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-extrabold gradient-bg text-white hover:opacity-85 transition-opacity disabled:opacity-50 glow-sm">
                    <LogIn className="h-3.5 w-3.5" /> {busy === a.id ? "Opening…" : "Access"}
                  </button>
                </TD>
              </TR>
            );
          })}
        </TableShell>
      ) : (
        <TableShell testid="accounts-table" head={["Account", "Type", "Hierarchy", "Access", "Websites", "Status", "Actions"]}>
          {accounts.length === 0 && <TR testid="accounts-empty"><TD><p className="text-sm text-muted-foreground py-4">No accounts found for this filter.</p></TD><TD /><TD /><TD /><TD /><TD /><TD /></TR>}
          {accounts.map((a) => (
            <TR key={a.id} testid={`account-row-${a.id}`}>
              <TD><div><p className="font-bold text-sm">{a.name}</p><p className="text-xs text-muted-foreground">{a.email}{a.phone ? ` · ${a.phone}` : ""}</p></div></TD>
              <TD><span className="text-xs font-bold px-2.5 py-1 rounded-full border border-border">{ROLE_BADGE[a.role]}</span></TD>
              <TD className="text-xs font-semibold text-muted-foreground">
                {a.role === "SUPER_ADMIN" ? `${a.partners_count ?? 0} partner${(a.partners_count ?? 0) === 1 ? "" : "s"}` : a.role === "PARTNER" ? `SA: ${a.super_admin_name || "—"}` : `Partner: ${a.partner_name || "—"}${a.super_admin_name && a.super_admin_name !== "—" ? ` · SA: ${a.super_admin_name}` : ""}`}
              </TD>
              <TD>
                <div className="space-y-1">
                  <DayChip days={a.days_left} />
                  {a.access_expires_at && <p className="text-[10px] text-muted-foreground flex items-center gap-1"><CalendarClock className="h-3 w-3" />{fmtDate(a.access_expires_at)}</p>}
                </div>
              </TD>
              <TD className="text-xs font-semibold">
                {a.websites_count ?? 0} site{(a.websites_count ?? 0) === 1 ? "" : "s"}
                {a.website_names?.length > 0 && <p className="text-[10px] text-muted-foreground max-w-44 truncate" title={a.website_names.join(", ")}>{a.website_names.slice(0, 3).join(", ")}{a.website_names.length > 3 ? "…" : ""}</p>}
              </TD>
              <TD><StatusBadge status={a.account_state} /></TD>
              <TD>
                <div className="flex flex-wrap gap-1.5">
                  <button data-testid={`access-account-${a.id}`} disabled={busy === a.id} onClick={() => access(a)} title="Access Account"
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-extrabold gradient-bg text-white hover:opacity-85 transition-opacity disabled:opacity-50 glow-sm">
                    <LogIn className="h-3.5 w-3.5" /> {busy === a.id ? "Opening…" : "Access"}
                  </button>
                  {(a.account_state !== "active" && a.role !== "CUSTOMER") && (
                    <button data-testid={`renew-${a.id}`} disabled={busy === a.id} onClick={() => setRenewTarget(a)}
                      className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-extrabold border border-emerald-500/40 text-emerald-600 hover:bg-emerald-500/10 transition-colors">
                      <RefreshCw className="h-3.5 w-3.5" /> Renew {RENEW_DAYS[a.role]}d
                    </button>
                  )}
                  {a.renewal_count > 0 && (
                    <button data-testid={`history-${a.id}`} onClick={() => setHistoryFor(a)} title="Renewal history"
                      className="h-8 w-8 rounded-full border border-border hover:bg-muted flex items-center justify-center transition-colors">
                      <History className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </TD>
            </TR>
          ))}
        </TableShell>
      )}

      <p className="mt-6 flex items-center gap-2 text-xs text-muted-foreground" data-testid="access-audit-note">
        <ShieldCheck className="h-4 w-4" /> Root-only feature — passwords are never changed or exposed, expired accounts keep all data, and every access/renewal is audit-logged.
      </p>

      <Dialog open={!!renewTarget} onOpenChange={() => setRenewTarget(null)}>
        <DialogContent className="sm:max-w-md" data-testid="renew-confirm-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Renew {renewTarget?.name}?</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to renew? This grants exactly <b className="text-foreground">{RENEW_DAYS[renewTarget?.role] || 30} days</b> of access
              starting from {renewTarget?.days_left > 0 ? "the current expiry date" : "today"}. Status becomes Active, all websites and data stay intact,
              and the renewal is recorded in history.
            </p>
            <div className="flex gap-3">
              <button data-testid="renew-cancel" onClick={() => setRenewTarget(null)}
                className="flex-1 py-2.5 rounded-full text-sm font-bold border border-border hover:bg-muted transition-colors">Cancel</button>
              <button data-testid="renew-confirm-btn" onClick={renew} disabled={busy === renewTarget?.id}
                className="flex-1 py-2.5 rounded-full text-sm font-extrabold gradient-bg text-white glow-sm hover:opacity-90 transition-opacity disabled:opacity-50">
                {busy === renewTarget?.id ? "Renewing…" : `OK — Renew ${RENEW_DAYS[renewTarget?.role] || 30} Days`}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyFor} onOpenChange={() => setHistoryFor(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">Renewal History — {historyFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2.5 mt-2 max-h-80 overflow-y-auto" data-testid="renewals-dialog">
            {(historyFor?.renewals || []).slice().reverse().map((r, i) => (
              <div key={i} className="border border-border rounded-md p-3.5 text-xs space-y-1" data-testid={`renewal-entry-${i}`}>
                <div className="flex justify-between font-bold">
                  <span>Renewal #{(historyFor.renewals.length) - i} · +{r.duration_days} days</span>
                  <span className="text-emerald-600">{r.payment_status === "manual_confirmed" ? "Manual payment confirmed" : r.payment_status}</span>
                </div>
                <p className="text-muted-foreground">Previous expiry: {r.prev_expiry ? fmtDate(r.prev_expiry) : "—"} → New expiry: <span className="font-bold text-foreground">{fmtDate(r.new_expiry)}</span></p>
                <p className="text-muted-foreground">Renewed {fmtDate(r.renewed_at)} by {r.renewed_by}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
